/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_VERTICALS, DEFAULT_PRIORITY, isOverdue, priorityMeta, type Task } from '@/types/task';
import type { Vertical } from '@/types/session';

/**
 * Filters, search and sort — kept in the URL rather than in module variables.
 *
 * Two things this buys that the vanilla app cannot do: a filtered board is a link
 * somebody can send, and the back button undoes a filter. `boardSort` also
 * mirrors to localStorage under its **existing key**, because renaming a storage
 * key silently discards everyone's saved order and shows them nothing new in
 * exchange.
 *
 * The active circle is deliberately NOT here. It is server state, persisted per
 * person; a link carrying it would silently switch the circle of whoever opened
 * it.
 */
export const BOARD_SORTS = ['due', 'priority', 'title', 'manual'] as const;
export type BoardSort = (typeof BOARD_SORTS)[number];

const SORT_KEY = 'kanbanBoardSort';

/**
 * Board order lives in localStorage, **not in the URL** — the one exception to
 * the paragraph above.
 *
 * It was in the URL, and that quietly made the stored value unreachable: a URL
 * carrying `sort=title` won on every load, so a corrupted stored value never got
 * to fall back and `board-view` caught it. Two reasons to leave it out rather
 * than paper over that:
 *
 *  - **Order is a preference, not a view of the data.** Filters and search change
 *    *what* somebody is looking at and are worth sharing; order changes only how
 *    it is stacked, and a link that reorders the recipient's board is a surprise.
 *  - The key is `kanbanBoardSort` and stays that way. Renaming a storage key
 *    silently discards every person's saved order and shows them nothing new.
 *
 * An external store rather than component state, because the select is in the
 * topbar and the columns it reorders are three components away.
 */
const sortListeners = new Set<() => void>();

function storedSort(): BoardSort {
  const saved = localStorage.getItem(SORT_KEY);
  return BOARD_SORTS.includes(saved as BoardSort) ? (saved as BoardSort) : 'due';
}

/**
 * The same function, exported under a name that says why it is exported.
 *
 * `storedSort` is an implementation detail of the hook and should stay one; the
 * fallback it implements is not — a corrupted value falling back to due date
 * rather than throwing is a rule, and rules get tested.
 */
export const storedSortForTest = storedSort;

function writeSort(next: BoardSort): void {
  localStorage.setItem(SORT_KEY, next);
  for (const notify of sortListeners) notify();
}

function subscribeSort(notify: () => void): () => void {
  sortListeners.add(notify);
  // `storage` covers the same person with the app open twice: changing the order
  // in one tab and finding the other still stacked the old way reads as a bug.
  window.addEventListener('storage', notify);
  return () => {
    sortListeners.delete(notify);
    window.removeEventListener('storage', notify);
  };
}

export interface BoardFilters {
  q: string;
  vertical: string;
  owner: string;
  tag: string;
  /** Empty means every priority; otherwise a `PRIORITIES` id as a string ('1'..'5'). */
  priority: string;
  /** One-click "just what's late", per isOverdue in types/task.ts. */
  overdue: boolean;
  sort: BoardSort;
}

export function useBoardFilters() {
  const [params, setParams] = useSearchParams();
  // Server snapshot is 'due': the same fallback a browser with nothing stored
  // gets, so a server-rendered first paint and the hydrated one agree.
  const sort = useSyncExternalStore(subscribeSort, storedSort, () => 'due' as BoardSort);

  const filters: BoardFilters = useMemo(
    () => ({
      q: params.get('q') ?? '',
      vertical: params.get('vertical') ?? '',
      owner: params.get('owner') ?? '',
      tag: params.get('tag') ?? '',
      priority: params.get('priority') ?? '',
      overdue: params.get('overdue') === 'true',
      sort,
    }),
    [params, sort],
  );

  const setFilter = useCallback(
    (patch: Partial<BoardFilters>) => {
      if (patch.sort) writeSort(patch.sort);
      const urlPatch = Object.entries(patch).filter(([key]) => key !== 'sort');
      if (urlPatch.length === 0) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of urlPatch) {
            // An empty filter leaves the URL entirely rather than sitting there as
            // `&tag=`, so a shared link says only what was actually chosen.
            if (!value) next.delete(key);
            else next.set(key, String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clear = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of ['q', 'vertical', 'owner', 'tag', 'priority', 'overdue']) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  // Sort is not a filter: it changes the order, never what is on the board, so it
  // must not light up Clear or count towards "3 filters active".
  const activeCount =
    (filters.q ? 1 : 0) + (filters.vertical ? 1 : 0) + (filters.owner ? 1 : 0) + (filters.tag ? 1 : 0)
    + (filters.priority ? 1 : 0) + (filters.overdue ? 1 : 0);

  return { filters, setFilter, clear, activeCount };
}

/**
 * What the search box looks at.
 *
 * A previous version of this claimed to match the vanilla app's
 * `taskMatchesQuery` and named five fields; that function reads **nine**, and the
 * four it left out are not decoration:
 *
 *  - the **priority label and name**, so "p1" or "critical" finds urgent work —
 *    `list-view` asserts this, and it is the only way to search by priority at all
 *  - the **vertical label**, so "complete" finds finished work — and, since a
 *    circle may rename its columns, so "shipped" finds it on a board that calls
 *    it that. Hence the `verticals` parameter: the labels are no longer a
 *    constant this file can import, and defaulting to the five keeps every
 *    caller that has no circle in hand (and every unit test) working unchanged
 *  - the **circle name**, which a pinned-in task still carries
 *  - **who created it**, which is how people find a task they only half remember
 *
 * `sowNumber` is this app's own addition and stays: it is a plain indexed column
 * precisely so it can be searched, unlike the encrypted title and description.
 *
 * One flattened string rather than nine `includes` calls, so a query spanning two
 * fields cannot accidentally match — the vanilla version joins with a space for
 * the same reason.
 */
export function matchesQuery(task: Task, query: string, verticals: Vertical[] = DEFAULT_VERTICALS): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const prio = priorityMeta(task.priority);
  return [
    task.title,
    task.description ?? '',
    task.sowNumber,
    verticals.find((v) => v.id === task.vertical)?.label ?? '',
    task.created_by_name ?? '',
    prio.label,
    prio.name,
    task.assignees.join(' '),
    task.participants.join(' '),
    task.tags.map((t) => t.name).join(' '),
    task.circle?.name ?? '',
  ]
    .join(' ')
    .toLowerCase()
    .includes(q);
}

export function applyFilters(tasks: Task[], f: BoardFilters, verticals: Vertical[] = DEFAULT_VERTICALS): Task[] {
  return tasks.filter((t) => {
    if (f.vertical && t.vertical !== f.vertical) return false;
    // **Either role.** The dropdown asks "whose work is this", and somebody
    // watching a task is still on it — a filter that silently ignored
    // participants would hide work that is plainly on screen under their name.
    if (f.owner && !t.assignees.includes(f.owner) && !t.participants.includes(f.owner)) return false;
    if (f.tag && !t.tags.some((tag) => tag.id === f.tag)) return false;
    if (f.priority && String(t.priority ?? DEFAULT_PRIORITY) !== f.priority) return false;
    if (f.overdue && !isOverdue(t)) return false;
    return matchesQuery(t, f.q, verticals);
  });
}

/**
 * A task with no date sorts last: an empty string would otherwise beat every real
 * date and put undated work at the top. `position` is the stable tie-break, so a
 * column does not reshuffle itself between renders.
 */
function byDueDate(a: Task, b: Task): number {
  const ad = a.finish_date || '9999-12-31';
  const bd = b.finish_date || '9999-12-31';
  return ad.localeCompare(bd) || a.position - b.position;
}

export function sortTasks(tasks: Task[], sort: BoardSort): Task[] {
  const out = [...tasks];
  switch (sort) {
    case 'priority':
      // Ascending, because P1 is the most urgent — and **within one priority the
      // nearer deadline still wins**, or P1 is an unordered pile and the sort has
      // answered "which of these matters" with "these ten do".
      return out.sort((a, b) => (a.priority ?? 4) - (b.priority ?? 4) || byDueDate(a, b));
    case 'title':
      // `base` so case is not a sort order, `numeric` so "Sprint 10" comes after
      // "Sprint 9" — the same collator the vanilla app uses.
      return out.sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base', numeric: true }),
      );
    case 'due':
      return out.sort(byDueDate);
    case 'manual':
    default:
      return out.sort((a, b) => a.position - b.position);
  }
}
